import React from 'react';
import {Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Paper, Typography} from '@mui/material';
import {Add, Check, ChevronLeftRounded, HubOutlined, SearchOffOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {SettingsDialog, SettingsDialogDescription} from '../SettingsDialog';
import {getSettingsColors, SettingsEmptyState, settingsTableStyles} from '../SettingsDesign';
import Spinner from '../../spinner/Spinner';

export function IntegrationBackButton({sx = [], ...props}) {
    return <Button {...props} type="button" variant="outlined" color="inherit" size="small" disableElevation
        startIcon={<ChevronLeftRounded/>} sx={[{
            minWidth: 88, minHeight: 36, px: 1.5, borderRadius: '8px', color: 'text.primary', borderColor: 'divider',
            bgcolor: 'transparent', boxShadow: 'none',
            '&:hover': {bgcolor: theme => getSettingsColors(theme).inset, borderColor: 'primary.main', boxShadow: 'none'},
            '&.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2}
        }, ...(Array.isArray(sx) ? sx : [sx])]}>
        Back
    </Button>;
}

export function IntegrationDialog(props) {
    return <SettingsDialog {...props} sx={[{
        '& .MuiDialogContent-root': {position: 'relative', minHeight: 0},
        '& .integration-body': {padding: 0},
        '& .MuiStepper-root': {bgcolor: theme => getSettingsColors(theme).inset, borderRadius: '10px', p: 2, mb: 3, overflowX: 'auto'},
        '& .MuiStepLabel-label': {fontSize: 12, whiteSpace: 'normal'},
        '& .MuiStep-root': {minWidth: 0, px: {xs: 0.5, sm: 1}},
        '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {mt: 1, overflowWrap: 'anywhere'},
        '& .MuiStepConnector-line': {borderColor: 'divider'},
        '& .MuiList-root': {bgcolor: theme => getSettingsColors(theme).inset, borderRadius: '10px', p: 1.5, my: 2},
        '& .MuiListItem-root': {px: 0, py: 0.5, alignItems: 'flex-start', fontSize: 13},
        '& .MuiListItemIcon-root': {minWidth: 32, pt: 0.25},
        '& .MuiListItemText-primary': {fontSize: 13, whiteSpace: 'normal', overflowWrap: 'anywhere'},
        '& .MuiDialogActions-root > .MuiBox-root': {width: '100%', minWidth: 0, gap: 1, flexWrap: 'wrap', alignItems: 'center'},
        '& .MuiDialogActions-root > .MuiBox-root > .MuiBox-root': {m: 0},
        '& .MuiDialogActions-root > .MuiBox-root > .MuiBox-root:first-of-type': {mr: 'auto'},
        '& .MuiDialogActions-root .MuiButton-root': {height: 36, minHeight: 36, px: 1.5, fontSize: 13, whiteSpace: 'nowrap'},
        '& p': {fontSize: 13, color: 'text.secondary', lineHeight: 1.65},
        '& .MuiAlert-root': {borderRadius: '8px'},
        '& .MuiAlert-filledWarning': {bgcolor: theme => alpha(theme.palette.warning.main, 0.14),
            color: theme => theme.palette.mode === 'dark' ? theme.palette.warning.light : '#8a4500'},
        '& .MuiTableContainer-root': {borderRadius: '8px'},
        '& .MuiTableCell-head': {bgcolor: theme => getSettingsColors(theme).inset, color: 'text.secondary', fontSize: 12},
        '& .MuiTableCell-body': {fontSize: 13}
    }, ...(Array.isArray(props.sx) ? props.sx : [props.sx || {}])]}/>;
}

export function IntegrationAuthorization({name}) {
    return <>
        <DialogTitle id="form-add-integration">Connect {name}
            <SettingsDialogDescription>Authorize Skro to connect your advertising account.</SettingsDialogDescription>
        </DialogTitle>
        <DialogContent>
            <Box role="status" sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 2, py: 4}}>
                <Spinner size={32}/>
                <Typography sx={{fontSize: 14, color: 'text.secondary', textAlign: 'center'}}>Opening {name} for authorization…</Typography>
            </Box>
        </DialogContent>
    </>;
}

export function IntegrationLogo({src, name}) {
    return <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 104, height: 44,
        flexShrink: 0, borderRadius: '8px', bgcolor: /white/i.test(src || '') ? '#101d29' : '#fff', px: 1.25,
        border: '1px solid', borderColor: theme => theme.palette.mode === 'light' ? theme.palette.divider : 'transparent'}}>
        <Box component="img" src={src} alt={name} sx={{maxWidth: '100%', maxHeight: 28, objectFit: 'contain'}}/>
    </Box>;
}

export function IntegrationHeader({name, logo, id}) {
    return <DialogTitle id={id}>
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5}}>
            <IntegrationLogo src={logo} name={name}/>
            <Box component="span">{name} integration</Box>
        </Box>
        <SettingsDialogDescription>Configure your connection and synchronization settings.</SettingsDialogDescription>
    </DialogTitle>;
}

export function IntegrationCard({children, ...props}) {
    return <Paper {...props} elevation={0} sx={{position: 'relative', p: {xs: 2, md: 3}, minWidth: 0,
        bgcolor: theme => getSettingsColors(theme).card, border: '1px solid', borderColor: 'divider', borderRadius: '12px',
        '& .MuiTypography-root': {whiteSpace: 'normal'}, '& .MuiButton-root': {borderRadius: '8px'},
        ...settingsTableStyles
    }}>{children}</Paper>;
}

export function IntegrationSectionHeader({title, description, onAdd, count}) {
    return <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2.5}}>
        <Box sx={{minWidth: 0, flex: '1 1 220px'}}>
            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1}}>
                <Typography component="h2" sx={{fontSize: 16, fontWeight: 600}}>{title}</Typography>
                {count != null && <Chip size="small" label={count} sx={{fontWeight: 600,
                    bgcolor: theme => getSettingsColors(theme).inset, color: 'text.secondary'}}/>}
            </Box>
            <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.75}}>{description}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add/>} onClick={onAdd}>Add integration</Button>
    </Box>;
}

export function IntegrationEmptyCard({title, description, onAdd, features = []}) {
    return <IntegrationCard>
        <IntegrationSectionHeader title={title} description={description} onAdd={onAdd} count={0}/>
        <SettingsEmptyState icon={HubOutlined} title="No integrations connected" description="Add an integration to connect your account and start syncing data."/>
        {features.length > 0 && <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
            gap: 1.5, p: 2, borderRadius: '10px', bgcolor: theme => getSettingsColors(theme).inset}}>
            {features.map(feature => <Box key={feature} sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                <Check sx={{fontSize: 18, color: 'primary.main', mt: 0.25}}/><Typography sx={{fontSize: 13, color: 'text.secondary'}}>{feature}</Typography>
            </Box>)}
        </Box>}
    </IntegrationCard>;
}

export function IntegrationProviderGrid({templates, onSelect}) {
    if (!templates?.length) return <SettingsEmptyState icon={SearchOffOutlined} title="No providers found" description="Try a different provider name."/>;
    return <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))'}, gap: 1.5, mt: 2}}>
        {templates.map(item => <Button key={item.id || item.name} onClick={() => onSelect(item)} color="inherit"
            sx={{display: 'flex', flexDirection: 'column', gap: 1.25, py: 2, px: 1, minWidth: 0,
                bgcolor: theme => getSettingsColors(theme).inset, border: '1px solid', borderColor: 'divider',
                '&:hover': {borderColor: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.08)},
                '&.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2}}}>
            {item.logoUrl && <IntegrationLogo src={item.logoUrl} name={item.name}/>}
            <Typography component="span" sx={{fontSize: 13, fontWeight: 500, whiteSpace: 'normal', textTransform: 'none'}}>{item.name}</Typography>
        </Button>)}
    </Box>;
}

export function IntegrationDeleteDialog({question, text, confirm}) {
    return <SettingsDialog open fullWidth maxWidth="xs" onClose={() => confirm(false)}
        aria-labelledby="integration-delete-title" aria-describedby="integration-delete-description"
        sx={{'& .MuiDialog-paper': {borderRadius: '16px'}}}>
        <DialogTitle id="integration-delete-title">{question || 'Delete integration?'}</DialogTitle>
        <DialogContent><Typography id="integration-delete-description" sx={{fontSize: 14, color: 'text.secondary'}}>{text}</Typography></DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={() => confirm(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={() => confirm(true)}>Delete integration</Button>
        </DialogActions>
    </SettingsDialog>;
}
